import React, { useState, useEffect, useContext } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { doc, getDoc, setDoc, serverTimestamp } from 'firebase/firestore';
import { db } from '../firebase';
import { AuthContext } from '../context/AuthContext';

// Route: /invite/:code
// Make sure App.js has: <Route path="/invite/:code" element={<InvitePage />} />

const InvitePage = () => {
  const { code } = useParams();
  const navigate = useNavigate();
  const { user } = useContext(AuthContext);

  const [event, setEvent] = useState(null);
  const [eventId, setEventId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [rsvping, setRsvping] = useState(false);
  const [rsvpDone, setRsvpDone] = useState(false);

  useEffect(() => {
    const loadEvent = async () => {
      try {
        // 1. Look up invite code in top-level invites collection
        const inviteSnap = await getDoc(doc(db, 'invites', code));
        if (!inviteSnap.exists()) {
          setError('This invite link is invalid or has expired.');
          setLoading(false);
          return;
        }

        const { eventId } = inviteSnap.data();
        setEventId(eventId);

        // 2. Load the event
        const eventSnap = await getDoc(doc(db, 'events', eventId));
        if (!eventSnap.exists()) {
          setError('This event no longer exists.');
          setLoading(false);
          return;
        }

        setEvent({ id: eventSnap.id, ...eventSnap.data() });
      } catch (e) {
        setError('Something went wrong. Please try again.');
        console.error(e);
      } finally {
        setLoading(false);
      }
    };

    loadEvent();
  }, [code]);

  // After login, resume RSVP if there's a pending invite
  useEffect(() => {
    const pending = sessionStorage.getItem('pendingInvite');
    if (pending && user && event && eventId) {
      sessionStorage.removeItem('pendingInvite');
    }
  }, [user, event, eventId]);

  const handleRSVP = async (status) => {
    if (!user) {
      sessionStorage.setItem('pendingInvite', code);
      navigate('/login');
      return;
    }

    setRsvping(true);
    try {
      await setDoc(doc(db, 'events', eventId, 'rsvps', user.uid), {
        status,
        userName: user.displayName || user.email,
        userEmail: user.email,
        updatedAt: serverTimestamp(),
      });
      setRsvpDone(true);
    } catch (e) {
      alert('Failed to RSVP. Please try again.');
      console.error(e);
    } finally {
      setRsvping(false);
    }
  };

  const formatDate = (date) =>
    new Date(date).toLocaleDateString('en-US', {
      weekday: 'long', year: 'numeric', month: 'long', day: 'numeric',
    });

  // ── Loading ──
  if (loading) {
    return (
      <div style={{ textAlign: 'center', padding: '4rem' }}>
        <h2 style={{ color: '#6b7280' }}>Loading invite...</h2>
      </div>
    );
  }

  // ── Invalid ──
  if (error) {
    return (
      <div style={{ maxWidth: '480px', margin: '4rem auto', padding: '2rem', textAlign: 'center' }}>
        <div style={{ fontSize: '3rem', marginBottom: '1rem' }}>🔗</div>
        <h2 style={{ color: '#1f2937', marginBottom: '0.5rem' }}>Invalid Invite</h2>
        <p style={{ color: '#6b7280', marginBottom: '2rem' }}>{error}</p>
        <Link to="/" style={{
          backgroundColor: '#6366f1', color: 'white',
          padding: '0.75rem 1.5rem', borderRadius: '8px', textDecoration: 'none', fontWeight: 'bold',
        }}>Go Home</Link>
      </div>
    );
  }

  // ── RSVP confirmed ──
  if (rsvpDone) {
    return (
      <div style={{ maxWidth: '480px', margin: '4rem auto', padding: '2rem', textAlign: 'center' }}>
        <div style={{ fontSize: '3rem', marginBottom: '1rem' }}>🎉</div>
        <h2 style={{ color: '#1f2937', marginBottom: '0.5rem' }}>You're in!</h2>
        <p style={{ color: '#6b7280', marginBottom: '2rem' }}>
          Your RSVP for <strong>{event.title}</strong> has been saved.
        </p>
        <Link to={`/events/${eventId}`} style={{
          backgroundColor: '#6366f1', color: 'white',
          padding: '0.75rem 1.5rem', borderRadius: '8px', textDecoration: 'none', fontWeight: 'bold',
        }}>View Event Details</Link>
      </div>
    );
  }

  // ── Main invite page ──
  return (
    <div style={{ maxWidth: '520px', margin: '3rem auto', padding: '0 1.5rem' }}>

      <div style={{ textAlign: 'center', marginBottom: '2rem' }}>
        <span style={{
          backgroundColor: '#ede9fe', color: '#7c3aed',
          padding: '0.35rem 1rem', borderRadius: '999px',
          fontSize: '0.85rem', fontWeight: 'bold',
        }}>You've been invited</span>
      </div>

      {/* Event card */}
      <div style={{
        backgroundColor: 'white', borderRadius: '12px',
        boxShadow: '0 4px 16px rgba(0,0,0,0.1)', overflow: 'hidden', marginBottom: '1.5rem',
      }}>
        {event.imageUrl && (
          <img src={event.imageUrl} alt={event.title}
            style={{ width: '100%', height: '200px', objectFit: 'cover' }} />
        )}
        <div style={{ padding: '1.5rem' }}>
          <h1 style={{ margin: '0 0 0.5rem 0', color: '#1f2937', fontSize: '1.5rem' }}>{event.title}</h1>
          <span style={{
            backgroundColor: '#dbeafe', color: '#1e40af',
            padding: '0.2rem 0.6rem', borderRadius: '12px', fontSize: '0.8rem',
          }}>{event.category}</span>

          <div style={{ marginTop: '1.25rem', display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
            <p style={{ margin: 0, color: '#374151' }}>📅 <strong>{formatDate(event.date)}</strong></p>
            <p style={{ margin: 0, color: '#374151' }}>🕐 {event.startTime} - {event.endTime}</p>
            <p style={{ margin: 0, color: '#374151' }}>📍 {event.location?.address}, {event.location?.city}</p>
            <p style={{ margin: 0, color: '#6b7280' }}>👤 Hosted by {event.creatorName || 'Event Creator'}</p>
          </div>

          {event.description && (
            <div style={{ marginTop: '1rem', paddingTop: '1rem', borderTop: '1px solid #e5e7eb' }}>
              <p style={{ color: '#6b7280', lineHeight: '1.6', margin: 0 }}>{event.description}</p>
            </div>
          )}
        </div>
      </div>

      {/* Login prompt */}
      {!user && (
        <div style={{
          backgroundColor: '#fef3c7', border: '1px solid #fde68a',
          borderRadius: '8px', padding: '1rem', marginBottom: '1.5rem',
          fontSize: '0.9rem', color: '#92400e', textAlign: 'center',
        }}>
          <Link to="/login" style={{ color: '#6366f1', fontWeight: 'bold' }}>Sign in</Link> or{' '}
          <Link to="/signup" style={{ color: '#6366f1', fontWeight: 'bold' }}>create an account</Link> to RSVP.
        </div>
      )}

      {/* RSVP buttons */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
        <button onClick={() => handleRSVP('attending')} disabled={rsvping} style={{
          backgroundColor: '#6366f1', color: 'white', padding: '0.875rem',
          border: 'none', borderRadius: '8px', fontWeight: 'bold',
          fontSize: '1rem', cursor: rsvping ? 'not-allowed' : 'pointer', opacity: rsvping ? 0.7 : 1,
        }}>✅ Yes, I'll be there!</button>
        <button onClick={() => handleRSVP('maybe')} disabled={rsvping} style={{
          backgroundColor: 'white', color: '#374151', padding: '0.875rem',
          border: '1.5px solid #e5e7eb', borderRadius: '8px', fontWeight: 'bold',
          fontSize: '1rem', cursor: rsvping ? 'not-allowed' : 'pointer', opacity: rsvping ? 0.7 : 1,
        }}>🤔 Maybe</button>
        <button onClick={() => handleRSVP('not_attending')} disabled={rsvping} style={{
          backgroundColor: 'white', color: '#9ca3af', padding: '0.875rem',
          border: '1.5px solid #e5e7eb', borderRadius: '8px',
          fontSize: '1rem', cursor: rsvping ? 'not-allowed' : 'pointer', opacity: rsvping ? 0.7 : 1,
        }}>❌ Can't make it</button>
      </div>
    </div>
  );
};

export default InvitePage;
